import React, { useEffect, useState } from "react";
import { getDetailsForPurchasePrint } from "../helpers/PurchaseReceiptHelper";
import { getImageURL } from "../helpers/ImageHelper";
import { useTranslation } from "react-i18next";

export default function PrintPurchasePage() {
  const { t } = useTranslation();
  const printDetails = getDetailsForPurchasePrint() || {};

  const {
    purchase = null,
    storeSettings = {},
    printSettings = {},
    currency = ""
  } = printDetails;

  const {
    store_name = "",
    address = "",
    phone = "",
    email = "",
    store_image: storeImage,
  } = storeSettings;

  const {
    page_format = 80,
    show_store_details = 1,
  } = printSettings;

  const is58 = Number(String(page_format).replace(/,/g, "")) <= 58;
  const fmt = (v) => `${currency}${Number(String(v || 0).replace(/,/g, "")).toFixed(2)}`;

  if (!purchase) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">No purchase details found to print.</p>
      </div>
    );
  }

  const todayIso = new Date(purchase.purchase_date || new Date()).toISOString().substring(0, 10);

  return (
    <>
      <style>{`
        .receipt-root {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: #000;
          background: #fff;
          margin: 0 auto;
        }
        .receipt-inner {
          box-sizing: border-box;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
        }
        .hr {
          border-bottom: 1px dashed #222;
          margin: 12px 0;
        }
        .center {
          text-align: center;
        }
        .muted {
          color: #444;
          font-size: 13px;
          font-weight: 600;
        }
        .mono {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", "Courier New", monospace;
        }
        .invoice-title {
          font-size: 24px;
          font-weight: 800;
          text-transform: uppercase;
          margin: 10px 0;
          letter-spacing: 1px;
        }
        .table-items {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        .table-items th {
          border-bottom: 2px solid #000;
          padding: 8px;
          text-align: left;
          font-weight: 700;
          font-size: 14px;
        }
        .table-items td {
          padding: 8px;
          border-bottom: 1px solid #eee;
          font-size: 14px;
        }
        .totals-section {
          float: right;
          width: 300px;
          margin-top: 15px;
        }
        .totals-row {
          display: flex;
          justify-content: space-between;
          margin: 6px 0;
          font-size: 15px;
          font-weight: 700;
        }
        .totals-row.large {
          font-size: 20px;
          font-weight: 900;
          border-top: 2px solid #000;
          padding-top: 6px;
        }
        .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin: 20px 0;
        }
        .meta-box {
          border: 1px solid #ddd;
          padding: 12px;
          border-radius: 8px;
        }
        .meta-box h4 {
          margin: 0 0 6px 0;
          font-size: 14px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .meta-box p {
          margin: 4px 0;
          font-size: 15px;
          font-weight: 700;
        }
        .print-btn {
          display: block;
          width: 100px;
          margin: 20px auto;
          padding: 10px;
          background: #000;
          color: #fff;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          text-align: center;
        }
        @media print {
          .print-btn {
            display: none !important;
          }
          body {
            background: #fff;
          }
          .receipt-inner {
            padding: 0;
            max-width: 100%;
          }
        }
      `}</style>

      <div className="receipt-root" role="document">
        <button className="print-btn" onClick={() => window.print()}>Print</button>
        
        <div className="receipt-inner">
          {/* Store Details */}
          {show_store_details === 1 && (
            <div className="center">
              {storeImage && (
                <img
                  src={getImageURL(storeImage)}
                  alt="Store Logo"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    filter: "grayscale(100%)",
                    display: "block",
                    margin: "0 auto 10px auto"
                  }}
                />
              )}
              <div style={{ fontWeight: 900, fontSize: 22 }}>{store_name}</div>
              <div className="muted" style={{ marginTop: 4 }}>{address}</div>
              {(phone || email) && (
                <div className="muted" style={{ marginTop: 4 }}>
                  Phone: {phone} | Email: {email}
                </div>
              )}
            </div>
          )}

          <div className="hr" />

          <div className="center">
            <h2 className="invoice-title">Purchase Invoice</h2>
          </div>

          {/* Supplier and Invoice Meta */}
          <div className="meta-grid">
            <div className="meta-box">
              <h4>Supplier</h4>
              <p>{purchase.supplier_name}</p>
              {purchase.supplier_pan_no && (
                <p style={{ fontSize: "13px", color: "#555", fontWeight: "normal" }}>
                  VAT/PAN: {purchase.supplier_pan_no}
                </p>
              )}
            </div>
            <div className="meta-box">
              <h4>Invoice Details</h4>
              <p>Invoice No: {purchase.purchase_invoice_no || `Ref #${purchase.id}`}</p>
              <p>Date: {todayIso}</p>
              <p>Status: <span style={{ textTransform: "uppercase" }}>{purchase.payment_status}</span></p>
            </div>
          </div>

          {/* Items Table */}
          <table className="table-items">
            <thead>
              <tr>
                <th>Item Description</th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Unit Price</th>
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {purchase.items && purchase.items.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{item.item_title}</td>
                  <td style={{ textAlign: "right" }} className="mono">{item.quantity} {item.unit || ""}</td>
                  <td style={{ textAlign: "right" }} className="mono">{fmt(item.unit_price)}</td>
                  <td style={{ textAlign: "right" }} className="mono">{fmt(item.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals Section */}
          <div className="totals-section">
            <div className="totals-row">
              <div>Subtotal:</div>
              <div className="mono">{fmt(purchase.total_amount)}</div>
            </div>
            {Number(String(purchase.discount_amount || 0).replace(/,/g, "")) > 0 && (
              <div className="totals-row" style={{ color: "#2563eb" }}>
                <div>Discount:</div>
                <div className="mono">-{fmt(purchase.discount_amount)}</div>
              </div>
            )}
            <div className="totals-row large">
              <div>Net Total:</div>
              <div className="mono">{fmt((Number(String(purchase.total_amount).replace(/,/g, "")) || 0) - (Number(String(purchase.discount_amount || 0).replace(/,/g, "")) || 0))}</div>
            </div>
            <div className="totals-row">
              <div>Paid Amount:</div>
              <div className="mono" style={{ color: "green" }}>{fmt(purchase.paid_amount)}</div>
            </div>
            {Number(String(purchase.due_amount).replace(/,/g, "")) > 0 && (
              <div className="totals-row">
                <div>Due Amount:</div>
                <div className="mono" style={{ color: "red" }}>{fmt(purchase.due_amount)}</div>
              </div>
            )}
          </div>

          <div style={{ clear: "both" }}></div>

          {/* Footer Notes */}
          {purchase.notes && (
            <div style={{ marginTop: 30, padding: 12, background: "#f9f9f9", borderRadius: 8 }}>
              <h4 style={{ margin: "0 0 6px 0", fontSize: 13, color: "#666", textTransform: "uppercase" }}>Notes</h4>
              <p style={{ margin: 0, fontSize: 14, whiteSpace: "pre-wrap" }}>{purchase.notes}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
